import { useReducer, useEffect, useMemo, useCallback, useRef } from 'react'
import {
    Box,
    Button,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tab,
    Tabs,
    Typography,
} from '@mui/material'
import { LicenseInfo } from '@mui/x-license-pro'
import {
    useGridApiRef,
    Toolbar,
    ToolbarButton,
    GridColDef,
    GridPaginationModel,
} from '@mui/x-data-grid-pro'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { Grid } from '.'
import {
    DensityLarge,
    DensityMedium,
    DensitySmall,
    Save as SaveIcon,
    Search as SearchIcon,
    CheckCircle,
} from '@mui/icons-material'

LicenseInfo.setLicenseKey(
    '78289722ce54bd59055e8887bc9a39daTz0xMjcwMzksRT0xODA1MzI3OTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI='
)

const reducer = (state, newState) => ({ ...state, ...newState })

interface pinnedColumnsProps {
    left: string[]
    right: string[]
}

interface Props {
    data: any
    visibleColumns: any
    getRowData?: (data) => void
    columnWidths?: {
        [key: string]: number
    }
    getColumnHeaderVisibility?: (data) => void
    columnsAvailable?: any
    onRowSelectionModelChange?: any
    checkboxSelection?: boolean
    customColumnNames?: string[]
    pinnedColumns?: pinnedColumnsProps
    onColumnOrderChange?: any
    onUpdatedColumnsChange?: (onColumnOrderChange: any) => void
    columns?: any
    apiColumns?: any
    savePreferences?: any
    columnVisibilityModel?: any
    apiDensity?: any
    onDensityChange?: (density: string) => void
    sx?: {}
    rowCount?: number
    loading?: boolean
    paginationModel?: GridPaginationModel
    setPaginationModel?: (model: GridPaginationModel) => void
    paginationMode?: 'client' | 'server'
    pageSizeOptions?: any
    onApiSearch?: (searchText: any) => void
    quickSearchFields?: string[]
    pinnedRows?: any
    sortingMode?: 'client' | 'server'
    filterMode?: 'client' | 'server'
    onFilterModelChange?: (model: any) => void
}

const UniversalDataTable = ({
    data,
    visibleColumns,
    getRowData = (row) => row,
    columnWidths,
    getColumnHeaderVisibility = (column) => column,
    columnsAvailable,
    onRowSelectionModelChange,
    checkboxSelection = true,
    customColumnNames = [],
    pinnedColumns = { left: [], right: [] },
    onColumnOrderChange = () => {},
    onUpdatedColumnsChange,
    apiColumns,
    savePreferences,
    columnVisibilityModel,
    apiDensity,
    onDensityChange,
    sx,
    rowCount: propRowCount,
    loading,
    paginationModel,
    setPaginationModel,
    paginationMode = 'client',
    pageSizeOptions,
    quickSearchFields,
    onApiSearch,
    pinnedRows,
    sortingMode,
    filterMode,
    onFilterModelChange,
}: Props) => {
    const defaultState = {
        columns: [],
        rows: [],
        newColumns: apiColumns,
        // Grids default to compact density unless a saved preference overrides.
        defaultDensity: apiDensity || 'compact',
        anchorEl: null,
        densityIcon:
            apiDensity === 'standard' ? (
                <DensityMedium />
            ) : apiDensity === 'comfortable' ? (
                <DensityLarge />
            ) : (
                <DensitySmall />
            ),
    }
    const [state, setState] = useReducer(reducer, defaultState)
    const { rows, columns, newColumns, defaultDensity, anchorEl, densityIcon } =
        state

    const apiRef: any = useGridApiRef()

    useEffect(() => {
        if (data && data.length !== 0) {
            let filteredColumns: any

            if (visibleColumns && visibleColumns.length !== 0) {
                filteredColumns = Object?.keys(data[0])
                    .map((key: any) => {
                        return {
                            field: key,
                            headerName: key?.toUpperCase(),
                            width: columnWidths ? columnWidths[key] : 150,
                            type:
                                key !== 'engineCalibrationActive'
                                    ? 'string'
                                    : 'boolean',
                        }
                    })
                    .filter(
                        (key) => visibleColumns.includes(key.field) && key.field
                    )

                const filteredRows = data?.map((values) => ({
                    ...values,
                    id: values?.id,
                }))

                if (customColumnNames && customColumnNames.length !== 0) {
                    filteredColumns = filteredColumns.map((column, index) => ({
                        ...column,
                        headerName: customColumnNames[index].toUpperCase(),
                    }))
                }

                return setState({
                    columns: filteredColumns,
                    rows: filteredRows,
                })
            }

            const pageData = apiColumns
            if (apiColumns && apiColumns?.length !== 0) {
                filteredColumns = Object?.keys(data[0])
                    ?.map((key) => {
                        pageData?.find((col) => col.field === key)

                        return {
                            field: key,
                            headerName: pageData.headerName,
                            width: pageData.width,
                            isVisible: pageData.isVisible,
                        }
                    })
                    .filter(({ field }) =>
                        pageData.some((col) => col.field === field)
                    )

                const filteredRows = data?.map((values) => ({
                    ...values,
                    id: values?.id,
                }))

                if (pageData && pageData.length !== 0) {
                    filteredColumns = pageData?.map((column) => ({
                        ...column,
                        headerName: column.headerName,
                    }))
                }

                return setState({
                    ...state,
                    columns: filteredColumns,
                    rows: filteredRows,
                })
            }
        } else if (visibleColumns.length === 0) {
            let filteredColumns = Object?.keys(data[0])
                ?.map((key) => ({
                    field: key,
                    headerName: key?.toUpperCase(),
                    width: columnWidths ? columnWidths[key] : 150,
                }))
                .filter(({ field }) => visibleColumns.includes(field) && field)

            const filteredRows = data?.map((values) => ({
                ...values,
                key: values?.id,
            }))

            if (customColumnNames && customColumnNames.length !== 0) {
                filteredColumns = filteredColumns.map((column, index) => ({
                    ...column,
                    headerName: customColumnNames[index].toUpperCase(),
                }))
            }

            return setState({
                columns: filteredColumns,
                rows: filteredRows,
            })
        }
    }, [data, visibleColumns, columnsAvailable])

    const handleColumnOrderChange = (params, type) => {
        if (onUpdatedColumnsChange) {
            let updatedColumns = [...newColumns]

            if (type === 'resize') {
                const { field, headerName, width } = params.colDef
                updatedColumns =
                    apiRef &&
                    apiRef?.current
                        .getAllColumns()
                        .map((col) =>
                            col.field === field
                                ? { ...col, width, headerName }
                                : col
                        )
            } else if (type === 'order') {
                updatedColumns =
                    apiRef &&
                    apiRef?.current
                        .getAllColumns()
                        .map((col: any) => ({
                            field: col.field,
                            headerName: col.headerName,
                            width: col.width,
                            isVisible: col.isVisible,
                            type: col.type,
                        }))
                        .filter((col: any) => col.field !== '__check__')
            }

            setState({ ...state, newColumns: updatedColumns })
            onUpdatedColumnsChange(updatedColumns)
        }
    }

    const search = useCallback(() => {
        apiRef?.current?.showFilterPanel?.()
    }, [apiRef])

    useEffect(() => {
        if (!apiRef.current) return

        const unsubscribe = apiRef.current.subscribeEvent(
            'filterModelChange',
            (filterModel: any) => {
                if (!filterModel?.items || filterModel.items.length === 0) {
                    onApiSearch?.(null)
                    return
                }

                const item = filterModel.items[0]

                if (item?.value) {
                    const query = {
                        columnField: item.field,
                        operator: item.operator,
                        value: item.value,
                    }
                    onApiSearch?.(query)
                } else {
                    onApiSearch?.(null)
                }
            }
        )

        return () => unsubscribe?.()
    }, [apiRef.current])

    const handleClick = useCallback((event: any) => {
        const button = event.currentTarget
        const rect = button.getBoundingClientRect(event)
        setState({
            anchorEl: {
                top: rect.bottom,
                left: rect.left,
                width: event.currentTarget.offsetWidth,
                height: event.currentTarget.offsetHeight,
            },
        })
    }, [])

    const handleClose = useCallback(() => {
        setState({ anchorEl: null })
    }, [])

    const handleDensityChange = useCallback((newDensity) => {
        setState({ defaultDensity: newDensity })
        if (apiRef && apiRef?.currentapiRef && apiRef?.currentapiRef?.current) {
            apiRef?.currentapiRef?.current?.setDensity(newDensity)
        }
        if (newDensity === 'comfortable') {
            setState({ densityIcon: <DensityLarge /> })
        }
        if (newDensity === 'standard') {
            setState({ densityIcon: <DensityMedium /> })
        }
        if (newDensity === 'compact') {
            setState({ densityIcon: <DensitySmall /> })
        }
        handleClose()

        if (onDensityChange) {
            onDensityChange(newDensity)
        }
    }, [onDensityChange, handleClose, apiRef])

    const open = Boolean(anchorEl)

    const CustomToolbar = useCallback(() => {
        return (
            <Toolbar
            // style={{
            //     display: 'flex',
            //     justifyContent: 'flex-start',

            //     padding: 3,
            //     gap: 1,
            // }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        height: '100%',
                        gap: 0,
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        width: '100%',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'row-reverse',
                        }}
                    >
                        <Button
                            startIcon={<SaveIcon />}
                            onClick={savePreferences}
                            disabled={savePreferences ? false : true}
                            sx={{ color: 'primary' }}
                        >
                            Save Table Preferences
                        </Button>

                        <Button
                            onClick={search}
                            title="Search"
                            sx={{ color: 'primary' }}
                            startIcon={<SearchIcon />}
                        >
                            Search
                        </Button>
                        <Button
                            id="density-button"
                            aria-controls={open ? 'density-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleClick}
                            startIcon={densityIcon}
                        >
                            Density
                        </Button>
                        <Menu
                            id="density-menu"
                            anchorReference="anchorPosition"
                            anchorPosition={
                                anchorEl
                                    ? { top: anchorEl.top, left: anchorEl.left }
                                    : undefined
                            }
                            open={open}
                            onClose={handleClose}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            <MenuItem
                                onClick={() => handleDensityChange('compact')}
                            >
                                <ListItemIcon>
                                    <DensitySmall />
                                </ListItemIcon>
                                <ListItemText>Compact</ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleDensityChange('standard')}
                            >
                                <ListItemIcon>
                                    <DensityMedium />
                                </ListItemIcon>
                                <ListItemText>Standard</ListItemText>
                            </MenuItem>

                            <MenuItem
                                onClick={() =>
                                    handleDensityChange('comfortable')
                                }
                            >
                                <ListItemIcon>
                                    <DensityLarge />
                                </ListItemIcon>
                                <ListItemText>Comfortable</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>
            </Toolbar>
        )
    }, [
        open,
        anchorEl,
        densityIcon,
        savePreferences,
        search,
        handleClick,
        handleClose,
        handleDensityChange,
    ])

    const isServerPagination =
        paginationMode === 'server' &&
        propRowCount !== undefined &&
        paginationModel !== undefined &&
        setPaginationModel !== undefined

    const finalRowCount = isServerPagination ? propRowCount : rows.length

    // Stable references for DataGridPro props so the grid does not re-process
    // slots / styles / pagination config on every render.
    const slots = useMemo(() => ({ toolbar: CustomToolbar }), [CustomToolbar])
    const slotProps: any = useMemo(
        () => ({
            toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
            },
            // Filter panel: real data columns lead the column dropdown and a new
            // filter defaults to the first data column — the "Checkbox selection"
            // (__check__) entry is pushed to the end instead of being first.
            filterPanel: {
                getColumnForNewFilter: ({ currentFilters, columns }: any) => {
                    const used = (currentFilters || []).map((f: any) => f.field)
                    const first = columns.find(
                        (c: any) =>
                            c.field !== '__check__' &&
                            c.filterable !== false &&
                            !used.includes(c.field)
                    )
                    return first ? first.field : null
                },
                filterFormProps: {
                    // Drop the "Checkbox selection" (__check__) entry — it's not a
                    // meaningful filter column and otherwise sorts to the top.
                    filterColumns: ({
                        field,
                        columns,
                        currentFilters,
                    }: any) => {
                        // Never hide the column the current filter row is editing
                        // — excluding it collapses the Select's value and makes
                        // the "Columns" label vanish. Only hide OTHER used fields.
                        const used = (currentFilters || [])
                            .map((f: any) => f.field)
                            .filter((f: any) => f !== field)
                        return columns
                            .filter(
                                (c: any) =>
                                    c.field !== '__check__' &&
                                    c.filterable !== false &&
                                    !used.includes(c.field)
                            )
                            .map((c: any) => c.field)
                    },
                },
            },
        }),
        []
    )
    const initialState = useMemo(
        () => ({ pagination: { paginationModel: { pageSize: 50 } } }),
        []
    )
    const resolvedPageSizeOptions = useMemo(
        () => (pageSizeOptions ? [Number(pageSizeOptions)] : [10, 25, 50, 100]),
        [pageSizeOptions]
    )
    const handleCellClick = useCallback(
        ({ field, row }: any) => {
            if (field !== '__check__') {
                getRowData(row)
            }
        },
        [getRowData]
    )
    const gridSx = useMemo(
        () => ({
            '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
                // Subtle brand tint keeps row text readable on hover (was an
                // opaque green gradient that washed out the content).
                backgroundColor: 'rgba(139, 196, 65, 0.10)',
            },
            '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'rgba(255,255,255,0.03)',
            },
            // Don't let a wide grid push the page into horizontal overflow.
            width: '100%',
            border: 'none',
            background: 'transparent',
        }),
        []
    )

    // Autosize columns to their content on first load — UNLESS the user has
    // saved custom widths (non-default widths / explicit columnWidths).
    const hasSavedWidths = useMemo(() => {
        if (columnWidths && Object.keys(columnWidths).length > 0) return true
        return (
            Array.isArray(apiColumns) &&
            apiColumns.some((c: any) => c?.width && c.width !== 150)
        )
    }, [columnWidths, apiColumns])
    const didAutosize = useRef(false)
    useEffect(() => {
        if (hasSavedWidths || didAutosize.current) return
        if (!rows || rows.length === 0) return
        const t = setTimeout(() => {
            try {
                // Size each column to its own content (headers + longest cell)
                // so nothing is trimmed; do NOT expand to fill the grid width.
                apiRef?.current?.autosizeColumns?.({
                    includeHeaders: true,
                    includeOutliers: true,
                    expand: false,
                })
                didAutosize.current = true
            } catch {
                /* grid not ready yet */
            }
        }, 60)
        return () => clearTimeout(t)
    }, [rows, hasSavedWidths, apiRef])

    return (
        <Grid container>
            <DataGridPro
                apiRef={apiRef ? apiRef : null}
                columns={columns}
                rows={rows}
                onRowSelectionModelChange={onRowSelectionModelChange}
                pagination
                initialState={initialState}
                onCellClick={handleCellClick}
                onColumnVisibilityModelChange={(e) =>
                    getColumnHeaderVisibility(e)
                }
                pageSizeOptions={resolvedPageSizeOptions}
                checkboxSelection={checkboxSelection}
                disableRowSelectionOnClick
                pinnedColumns={pinnedColumns}
                onColumnOrderChange={(params) =>
                    handleColumnOrderChange(params, 'order')
                }
                onColumnResize={(params) =>
                    handleColumnOrderChange(params, 'resize')
                }
                disableColumnReorder={false}
                columnVisibilityModel={columnVisibilityModel}
                slots={slots}
                slotProps={slotProps}
                showToolbar={true}
                disableColumnMenu={true}
                rowCount={finalRowCount}
                loading={loading}
                paginationModel={paginationModel}
                paginationMode={paginationMode}
                onPaginationModelChange={setPaginationModel}
                density={defaultDensity}
                pinnedRows={pinnedRows}
                sx={gridSx}
                sortingMode={sortingMode}
                filterMode={filterMode}
                onFilterModelChange={onFilterModelChange}
            />
        </Grid>
    )
}

export default UniversalDataTable
